import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { trackEvent } from "@/lib/analytics";
import { useLanguage } from "@/i18n/useLanguage";
import Reveal from "@/components/Reveal";

const CallToAction = () => {
  const { t } = useTranslation();
  const { localize } = useLanguage();
  const sectionRef = useRef<HTMLElement | null>(null);
  // Normalized pointer offset from section center, range -1..1
  const [pointer, setPointer] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReducedMotion) return;

    const el = sectionRef.current;
    if (!el) return;

    let ticking = false;
    let nextX = 0;
    let nextY = 0;

    const apply = () => {
      setPointer({ x: nextX, y: nextY });
      ticking = false;
    };

    const onMove = (e: PointerEvent) => {
      const rect = el.getBoundingClientRect();
      const halfW = rect.width / 2;
      const halfH = rect.height / 2;
      const nx = (e.clientX - rect.left - halfW) / halfW;
      const ny = (e.clientY - rect.top - halfH) / halfH;
      nextX = Math.min(1, Math.max(-1, nx));
      nextY = Math.min(1, Math.max(-1, ny));
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(apply);
      }
    };

    const onLeave = () => {
      nextX = 0;
      nextY = 0;
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(apply);
      }
    };

    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerleave", onLeave);
    return () => {
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  const { x: nx, y: ny } = pointer;

  return (
    <section
      ref={sectionRef}
      className="relative py-16 sm:py-20 md:py-28 overflow-hidden bg-background"
    >
      {/* Floating blobs — driven by mouse movement */}
      <div
        aria-hidden
        className="absolute -top-32 left-1/4 w-[28rem] h-[28rem] rounded-full bg-primary/15 blur-3xl will-change-transform"
        style={{
          transform: `translate3d(${nx * 60}px, ${ny * 50}px, 0) scale(${1 + Math.abs(nx) * 0.08})`,
          filter: `blur(48px) hue-rotate(${nx * 12}deg)`,
          transition:
            "transform 500ms cubic-bezier(0.22, 1, 0.36, 1), filter 500ms ease-out",
        }}
      />
      <div
        aria-hidden
        className="absolute -bottom-32 right-1/4 w-[28rem] h-[28rem] rounded-full blur-3xl will-change-transform"
        style={{
          backgroundColor: "hsl(var(--accent-violet) / 0.28)",
          transform: `translate3d(${nx * -80}px, ${ny * -65}px, 0) scale(${1 + Math.abs(ny) * 0.1})`,
          filter: `blur(48px) hue-rotate(${nx * -16}deg)`,
          transition:
            "transform 500ms cubic-bezier(0.22, 1, 0.36, 1), filter 500ms ease-out",
        }}
      />

      {/* Soft dividers (matches PageHero) */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

      <div className="relative z-10 container mx-auto px-4 sm:px-6 text-center">
        <Reveal as="h2" className="text-3xl sm:text-4xl lg:text-6xl font-bold mb-6">
          {t("cta.title")}?
        </Reveal>


        <Reveal className="text-center" delay={150}>
          <Button
            variant="primary"
            size="lg"
            asChild
            className="transition-shadow duration-300 hover:shadow-glow-primary"
          >
            <Link
              to={localize("/contact")}
              onClick={() =>
                trackEvent("cta_click", { cta_id: "footer_book_consultation", location: "call_to_action" })
              }
            >
              {t("cta.primary")}
            </Link>
          </Button>
        </Reveal>
      </div>
    </section>
  );
};

export default CallToAction;
